import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class CommonMethodService {

    constructor(private toaster: ToastrService, private route: Router) {
    }

    checkFormValidOrNot(formControlName): void {
        const invalidFields: string[] = [];
        Object.keys(formControlName.controls).forEach(field => {
            const control = formControlName.get(field);
            control.markAsTouched({ onlySelf: true });
            if (control.invalid) {
              invalidFields.push(field);
            }
          });
        this.focusField(invalidFields.length > 0 ? invalidFields[0] : null);
        // Every failed validation path shows a toast, and it names the fields
        // that are wrong: a bare "complete all required fields" gives the
        // person no way to tell which of a long form's fields is the problem.
        if (invalidFields.length > 0) {
          const labels = invalidFields
            .map(field => this.fieldLabel(field))
            .filter((label, index, all) => all.indexOf(label) === index);
          const shown = labels.slice(0, 5).join(', ');
          const more = labels.length > 5 ? ' and ' + (labels.length - 5) + ' more' : '';
          this.toaster.error('Please complete: ' + shown + more + '.');
        }
    }

    // The label printed next to the field on screen, so the message matches
    // what the person sees. Falls back to the control name in words when the
    // field has no label (or is not on screen).
    private fieldLabel(fieldName: string): string {
        const el = document.querySelector('[formcontrolname="' + fieldName + '"]') as HTMLElement;
        const container = el ? el.closest('.vw-field, .form-group') : null;
        const label = container ? container.querySelector('label') : null;
        const text = label && label.textContent ? label.textContent.replace(/\*/g, '').replace(/\s+/g, ' ').trim() : '';
        if (text) {
          return text;
        }
        return fieldName.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
    }

    // Used across every form's failed-submit path (checkFormValidOrNot above)
    // so a user doesn't have to hunt the page for whichever required field
    // they missed — Angular renders formControlName as the lowercased
    // "formcontrolname" DOM attribute, which is what lets this find the
    // element without each form having to pass its own ElementRef/ViewChild.
    private focusField(fieldName: string): void {
        if (fieldName === null) {
          return;
        }
        const el = document.querySelector('[formcontrolname="' + fieldName + '"]') as HTMLElement;
        if (el && typeof el.focus === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
    }

    returnErrorMessage(error ): void {
       if ( error.status === 0) {
        this.toaster.error('Server is not reachable. Please try again!!!');
       } else if ( error.status >= 500) {
        const message = this.extractErrorMessage(error);
        this.toaster.error(message);
        console.error(error);
       } else if (error.status === 400 ) {
        const message = this.extractErrorMessage(error);
        this.toaster.error(message);
        console.error(error.error);
       } else if (error.status === 417 ) {
        const message = this.extractErrorMessage(error);
        this.toaster.error(message);
        console.error(error.error);
       } else if (error.status > 401 && error.status < 500) {
        const message = this.extractErrorMessage(error);
        this.toaster.error(message);
        console.error(error.error);
       } else if ( error.status >= 200  && error.status < 300) {
        this.toaster.success('Successfull');
        console.error('Successfull');
       } else if ( error.status === 401) {
        if ( error.error.detail === 'Bad credentials' ) {
            this.toaster.error('Invalid username or password');
        } else if ( error.error.detail === 'Full authentication is required to access this resource' ) {
            this.toaster.warning('Session has expired. Please log in again.');
        } else if ( error.error.detail && /not activated/i.test(error.error.detail) ) {
            this.toaster.error('Your account has not been activated yet. Please contact your administrator.');
        } else {
            const message = this.extractErrorMessage(error);
            this.toaster.error(message);
        }
        console.error(error.error.detail);
       } else {
        this.toaster.error('Something went wrong. Please try again latter!!!');
       }
    }

    private extractErrorMessage(error: any): string {
        if (!error || !error.error) {
            return 'Something went wrong. Please try again later.';
        }

        const errBody = error.error;

        // If error body is a Blob (responseType: 'blob'), try to read it as text
        if (errBody instanceof Blob) {
            const reader = new FileReader();
            reader.readAsText(errBody);
            // Synchronously return a fallback since FileReader is async
            return 'Something went wrong. Please try again later.';
        }

        // Backend error format: { respCode, respMessage, message, error, exception, path }
        if (errBody.respMessage && typeof errBody.respMessage === 'string') {
            return errBody.respMessage;
        }
        // JHipster's ProblemDetail body puts the human-readable text in
        // `detail` (e.g. "User com_admin was not activated") and reserves
        // `message` for an untranslated i18n key (e.g. "error.http.401")
        // meant for a frontend translation table, not for display as-is —
        // `detail` must be checked first or the user sees the raw key.
        if (errBody.detail && typeof errBody.detail === 'string') {
            return errBody.detail;
        }
        if (errBody.message && typeof errBody.message === 'string' && !/^error\.\S+$/.test(errBody.message)) {
            return errBody.message;
        }
        if (errBody.title && typeof errBody.title === 'string') {
            return errBody.title;
        }

        // DRF-style or other formats
        if (typeof errBody === 'string') {
            return errBody;
        }

        // Last resort: stringify the error body
        try {
            return JSON.stringify(errBody);
        } catch (e) {
            return 'Something went wrong. Please try again later.';
        }
    }

}
